<template>
    <section>
        <v-loading type="global" :data="Customer"></v-loading>

        <div class="o-wrapper o-wrapper--center">
            <router-link class="c-button c-button--outline c-button--small u-margin-right-small"
                         :to="{name: 'CustomerListOverview'}">{{ $t('global.back') }}
            </router-link>
        </div>

        <page-header>
            <slot slot="title">{{ Customer.Name }} ({{ Customer.AltKey }})</slot>
            <slot slot="description">
                Hier vindt u een overzicht van de klantgegevens van {{ Customer.Name }}
            </slot>
            <slot slot="action">
                <div v-if="isAdmin" class="u-pos-relative c-customer-search">
                    <h5 class="u-text-uppercase">{{ $t('customer_list.update_customer_key') }}</h5>
                    <v-input v-model="NewCustomerKey"
                             :horizontal="true"
                             :fluid="true"
                             :name="`${_uid}-customer-search`"
                             :placeholder="$t('customer_list.new_customer_key')"
                             custom-class="c-customer-search__input"
                             autocomplete="off"
                             type="search">
                    </v-input>
                    <div v-if="isSalesman" class="c-customer-list__button" style="padding-left: 165px; padding-top: 5px;">
                        <v-switch v-model="CustomerListOptions.myCustomersOnly"
                                  name="my-customers-only"
                                  @input="customerSearch">
                            <slot slot="label">{{ $t('customer_list.my_customers') }}</slot>
                        </v-switch>
                    </div> 
                    <div v-if="isAdmin">
                        <button type="button" class="c-button c-button--alpha" @click="editCustomerKey">{{ $t('customer_list.update') }}</button>
                    </div>
                </div>
            </slot>
        </page-header>

        <div class="o-wrapper o-wrapper--center">
            <section class="o-page-row--medium">
                <div class="o-grid o-grid--half o-gutter-medium">
                    <div>
                        <h3>Bedrijfsgegevens</h3>
                        <v-form-row>
                            <v-label>Bedrijfsnaam</v-label>
                            {{ Customer.Name }}
                        </v-form-row>
                        <v-form-row>
                            <v-label>Commerciele naam</v-label>
                            {{ Customer.CommercialName }}
                        </v-form-row>
                        <v-form-row>
                            <v-label>Adres</v-label>
                            {{ Customer.Street }} {{ Customer.Zipcode }} - {{ Customer.City }}, <strong>{{
                            Customer.CountryName }}</strong>
                        </v-form-row>
                        <v-form-row>
                            <v-label>Algemeen e-mailadres</v-label>
                            {{ Customer.Email }}
                        </v-form-row>
                        <v-form-row>
                            <v-label>Algemeen telefoonnummer</v-label>
                            {{ Customer.Phone }}
                        </v-form-row>
                    </div>

                    <div>
                        <h3>Extra bedrijfsgegevens</h3>
                        <v-form-row>
                            <v-label>Aantal facebook volgers</v-label>
                            {{ Customer.FacebookFollowers }}
                        </v-form-row>
                        <v-form-row>
                            <v-label>Aantal instagram volgers</v-label>
                            {{ Customer.InstagramFollowers }}
                        </v-form-row>
                        <v-form-row>
                            <v-label>Website</v-label>
                            {{ Customer.Url }}
                        </v-form-row>
                        <v-form-row>
                            <v-label>Webshop</v-label>
                            {{ Customer.WebshopUrl }}
                        </v-form-row>
                        <v-form-row>
                            <v-label>Kassa systeem</v-label>
                            {{ Customer.PointOfSaleSystem }}
                        </v-form-row>
                    </div>
                </div>
            </section>
            <section class="o-page-row--medium">
                <h3>Gebruikers</h3>
                <table-component v-if="CustomerUsers.length" class="o-page-row" table-class="c-table c-table--small"
                                 :data="CustomerUsers" :show-caption="false" :show-filter="false">
                    <table-column key="LastName" label="Naam" :sortable="false">
                        <template slot-scope="row">
                            {{row["LastName"]}}
                        </template>
                    </table-column>
                    <table-column key="FirstName" label="Voornaam" :sortable="false">
                        <template slot-scope="row">
                            {{row["FirstName"]}}
                        </template>
                    </table-column>
                    <table-column key="Email" label="E-mail" :sortable="false">
                        <template slot-scope="row">
                            {{row["Email"]}}
                        </template>
                    </table-column>
                    <table-column key="Role" label="Rol" :sortable="false">
                        <template slot-scope="row">
                            {{row["Role"]}}
                        </template>
                    </table-column>
                    <table-column key="Function" label="Functie" :sortable="false">
                        <template slot-scope="row">
                            <span v-if="row.Function === 1">Strateeg</span>
                            <span v-else>Zaakvoerder</span>
                        </template>
                    </table-column>
                </table-component>

                <div v-else class="u-font-large u-text-align-center u-padding">
                    Geen gebruikers gevonden voor deze klant<br>
                    <span class="u-font-default">Fout in de pagina? Neem contact op met de administrator.</span>
                </div>
            </section>

            <section class="o-page-row--medium">
                <h3>Dealer locator</h3>
                <ul class="o-grid--third o-grid o-list--reset u-m--15">
                    <li class="u-flex u-p-15" v-for="DealerLocatorAddress in DealerLocatorAddresses"
                        :key="DealerLocatorAddress.Id">
                        <article class="u-border u-padding-small u-fullwidth c-overlay-wrapper c-dealer-locator__item" :class="{ 'active': DealerLocatorAddress.ShowOnDealerLocator}">
                            <div class="c-overlay">
                                <template v-if="DealerLocatorAddress.ShowOnDealerLocator">
                                    <button @click="toggleDealerLocatorVisibility(DealerLocatorAddress)"
                                            class="c-button c-button--small c-button--danger">
                                        {{ $t('customer.account.addresses.shop.hide_on_dealer_locator') }}
                                    </button>
                                </template>
                                <template v-else>
                                    <button @click="toggleDealerLocatorVisibility(DealerLocatorAddress)"
                                            class="c-button c-button--small c-button--info">
                                        {{ $t('customer.account.addresses.shop.show_on_dealer_locator') }}
                                    </button>
                                </template>
                            </div>
                            <h3 class="u-margin-bottom-none u-font-inherit">
                                {{ DealerLocatorAddress.Header }}
                            </h3>

                            <p v-if="DealerLocatorAddress.Street || DealerLocatorAddress.City || DealerLocatorAddress.CountryName">
                                <span v-if="DealerLocatorAddress.Street">
                                    {{ DealerLocatorAddress.Street }}<br v-if="DealerLocatorAddress.City">
                                </span>
                                <span v-if="DealerLocatorAddress.City">
                                    {{ DealerLocatorAddress.ZipCode }} {{ DealerLocatorAddress.City }}<br
                                        v-if="DealerLocatorAddress.CountryName">
                                </span>
                                <span v-if="DealerLocatorAddress.CountryName">
                                    {{ DealerLocatorAddress.CountryName }}
                                </span>
                            </p>

                            <ul class="c-nav u-font">
                                <li v-if="DealerLocatorAddress.Email">
                                    <a :href="'mailto:' + DealerLocatorAddress.Email"
                                       class="c-icon-label u-text-truncate">
                                        <svgicon name="mail"></svgicon>
                                        <span>
                                            {{ DealerLocatorAddress.Email }}
                                        </span>
                                    </a>
                                </li>
                                <li v-if="DealerLocatorAddress.Phone">
                                    <span class="c-icon-label">
                                        <svgicon name="phone"></svgicon>
                                        <span>
                                        {{ DealerLocatorAddress.Phone }}
                                        </span>
                                    </span>
                                </li>
                            </ul>
                        </article>
                    </li>
                </ul>

                <v-modal ref="modal">
                    <slot slot="title">{{ $t('customer.account.dealer_locator.title') }}</slot>
                    <div class="o-wrapper o-wrapper--center">
                        <form action="" class="c-form c-form--horizontal">
                            <div class="c-form-row">
                                <div class="u-margin-bottom-small">
                                    <GmapMap
                                            :center="{
                                                lat: CurrentAddress.Latitude,
                                                lng: CurrentAddress.Longitude
                                            }"
                                            :zoom="18"
                                            :options="{

                                                }"
                                            style="width: 100%; height: 240px">
                                        <GmapMarker
                                                :position="{
                                                    lat: CurrentAddress.Latitude,
                                                    lng: CurrentAddress.Longitude
                                                }"
                                                :clickable="true"
                                                :draggable="true"
                                                @dragend="updatePosition"
                                        />
                                    </GmapMap>
                                </div>
                                <v-form-row>
                                    <label class="c-label">Weer te geven adres</label>
                                    <div class="u-inline-block u-pos-relative u-fullwidth">{{ CurrentAddress.Street }},
                                        {{ CurrentAddress.ZipCode }} {{ CurrentAddress.City }} ({{
                                        CurrentAddress.CountryName }})
                                    </div>
                                </v-form-row>
                                <v-form-row>
                                    <v-input v-model="CurrentAddress.Longitude" name="address_longitude" :fluid="true">
                                        <slot slot="label">{{ $t('customer.account.dealer_locator.longitude.label') }}
                                        </slot>
                                    </v-input>
                                </v-form-row>
                                <v-form-row>
                                    <v-input v-model="CurrentAddress.Latitude" name="address_latitude" :fluid="true">
                                        <slot slot="label">{{ $t('customer.account.dealer_locator.latitude.label') }}
                                        </slot>
                                    </v-input>
                                </v-form-row>
                                <v-form-row>
                                    <v-custom-select v-model="CurrentAddress.Brands"
                                                     :options="BrandsOptions"
                                                     option-name="Name"
                                                     option-value="Code"
                                                     name="branch"
                                                     search-label="Name"
                                                     track-by="Code"
                                                     :fluid="true"
                                                     :multiple="true">
                                        <slot slot="label">{{ $t('branch.label.brands') }}</slot>
                                    </v-custom-select>
                                </v-form-row>
                                <v-form-row>
                                    <v-input v-model="CurrentAddress.Url" name="address_url" :fluid="true">
                                        <slot slot="label">{{ $t('customer.account.dealer_locator.url.label') }}</slot>
                                    </v-input>
                                </v-form-row>
                                <v-form-row v-if="CurrentAddress.Url">
                                    <v-select custom-class="u-fullwidth" v-model.number="CurrentAddress.Type"
                                              :selected="CurrentAddress.Type"
                                              :disable-expanded="true"
                                              name="type"
                                              :options="[
                                                {value: 0, content: $t('customer.account.dealer_locator.type_website.website_without_webshop'), selected: CurrentAddress.Type === 0 ? true : undefined },
                                                {value: 1, content: $t('customer.account.dealer_locator.type_website.physical_store_with_webshop'), selected: CurrentAddress.Type === 1 ? true : undefined},
                                                {value: 2, content: $t('customer.account.dealer_locator.type_website.webshop_without_physical_store'), selected: CurrentAddress.Type === 2 ? true : undefined}]"
                                              :fluid="true">
                                        <slot slot="label">{{ $t('customer.account.dealer_locator.type_website.label')
                                            }}
                                        </slot>
                                    </v-select>
                                </v-form-row>
                                <v-form-row>
                                    <div class="c-form-item">
                                        <v-label></v-label>
                                        <button @click.prevent="showOnDealerLocator" class="c-button c-button--alpha">
                                            Verzenden
                                        </button>
                                    </div>
                                </v-form-row>
                            </div>
                        </form>
                    </div>
                </v-modal>
            </section>
        </div>

    </section>
</template>

<script>
    import Vue from "vue";
    import VModal from '@/components/modal/Modal'
    import VLoading from "@/components/Loading";
    import PageHeader from "@/components/common/PageHeader";
    import VCustomSelect from '@/components/form/CustomSelect'
    import VFormRow from "@/components/form/Row";
    import VLabel from "@/components/form/Label";
    import VInput from '@/components/form/Input';
    import VSelect from '@/components/form/Select';

    // store
    import {mapGetters} from "vuex";

    export default {
        data() {
            return {
                CurrentAddress: {
                    Brands: [],
                },
                DealerLocatorAddressTypeOptions: [
                    {
                        value: 0,
                        content: 'Website zonder webshop'
                    },
                    {
                        value: 1,
                        content: 'Fysieke winkel met webshop'
                    },
                    {
                        value: 2,
                        content: 'Webshop zonder fysieke winkel'
                    }
                ],
                NewCustomerKey: null
            }
        },
        props: {},
        components: {
            PageHeader,
            VLoading,
            VModal,
            VFormRow,
            VLabel,
            VInput,
            VSelect,
            VCustomSelect
        },
        created() {
            this.$store.dispatch('getBrandsOptions');
            this.$store.dispatch("getCustomerById", this.$route.params.id).then(() => {
                this.$store.dispatch("searchUsers", { customerKey: this.Customer.Key });
                this.$store.dispatch("getDealerLocatorAddress", this.Customer.Key);
            });
        },
        computed: {
            ...mapGetters({
                Customer: "Customer",
                CustomerUsers: "Users",
                DealerLocatorAddresses: "dealerLocatorAddress",
                BrandsOptions: 'BrandsOptions',
            }),
            isAdmin() {
                return this.$store.getters.isAdmin;
            },
            isSalesman() {
                return this.$store.getters.isSalesman;
            },
        },
        methods: {
            toggleDealerLocatorVisibility(address) {
                if (!address.ShowOnDealerLocator) {
                    this.CurrentAddress = address;
                    this.$refs.modal.openDialog();
                } else {
                    this.$store.dispatch('hideOnDealerLocator', address.Id).then(() => {
                        this.$store.dispatch("getDealerLocatorAddress", this.Customer.Key);
                    })
                }
            },
            showOnDealerLocator() {
                this.$store.dispatch('showOnDealerLocator', this.CurrentAddress).then(() => {
                    this.$refs.modal.closeDialog();
                    this.$store.dispatch("getDealerLocatorAddress", this.Customer.Key);
                })
            },
            updatePosition(value) {
                this.$set(this.CurrentAddress, 'Latitude', value.latLng.lat());
                this.$set(this.CurrentAddress, 'Longitude', value.latLng.lng());
            },
            editCustomerKey(){
                this.$store.dispatch("editCustomerKey", {
                    "CurrentKey": this.Customer.Key,
                    "NewAltKey": this.NewCustomerKey
                }).then(() => {                    
                    this.$store.dispatch("getCustomerById", this.$route.params.id).then(() => {
                        this.$store.dispatch("searchUsers", { customerKey: this.Customer.Key });
                        this.$store.dispatch("getDealerLocatorAddress", this.Customer.Key);
                    });
                    this.$notify({
                        group: 'global',
                        title: this.$t('customer_list.update_customer_key_success', {'customer_key': this.NewCustomerKey}),
                        type: 'success',
                        duration: 4000
                    });
                    this.NewCustomerKey = null;
                }).catch((e) => {
                    this.$notify({
                        group: 'global',
                        title: e.ExceptionMessage,
                        type: 'error',
                        duration: 4000
                    });
                });
            }
        }
    };
</script>

<style lang="scss" scoped>
    @import "~@/assets/scss/private";

    .c-label {
        font-weight: 500;
        display: inline-block;
        width: 230px;
    }

    .u-m--15 {
        margin: -15px;
    }

    .u-p-15 {
        padding: 15px !important;
    }

    .c-overlay-wrapper {
        position: relative;
    }

    .c-overlay {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background-color: rgba(black, 0.75);
        transition: opacity 0.15s ease-out;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;

        > * {
            margin: 10px;
        }

        &:not(:hover) {
            opacity: 0;
        }
    }
.c-dealer-locator__item {
    &.active {
        border-color: $color-info !important;
        background-color: lighten($color-info, 35%);
    }
}
</style>


// WEBPACK FOOTER //
// src/views/salesman/customerList/CustomerListDetail.vue